// src/pages/CategoryPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ProductCard from '../components/product/ProductCard';
import SEOHelper from '../components/common/SEOHelper';

const KNOWN_SLUGS = [
  'nouveautes', 'capes', 'ensembles', 'abayas', 'burkinis',
  'robes', 'kimonos', 'hijabs', 'sacs'
];

function CategoryPage() {
  const { t } = useTranslation();
  const { categorie } = useParams();

  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);   // ⭐ NOUVEAU
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [sortBy, setSortBy]     = useState('default');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);

    const categoryName = categorie.charAt(0).toUpperCase() + categorie.slice(1);

    // Charger les produits de la catégorie
    axios
      .get(`https://madadline.com/api/products`, {
        params: { categorie: categoryName },
      })
      .then(res => {
        if (!mounted) return;
        const data = Array.isArray(res.data) ? res.data : [];
        setProducts(data);
      })
      .catch(err => {
        if (!mounted) return;
        console.error('Erreur catégorie:', err);
        setError(t('category.errorLoading'));
        setProducts([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    // ⭐ Charger aussi les infos de la catégorie (meta_title, meta_description)
    axios
      .get('https://madadline.com/api/categories')
      .then(({ data }) => {
        if (!mounted) return;
        const list = Array.isArray(data) ? data : [];
        const found = list.find(c =>
          c.slug === categorie || c.name.toLowerCase() === categorie.toLowerCase()
        );
        setCategory(found || null);
      })
      .catch(() => {
        if (mounted) setCategory(null);
      });

    return () => { mounted = false; };
  }, [categorie, t]);

  const getSortedProducts = () => {
    const sorted = [...products];
    if (sortBy === 'price-asc') {
      sorted.sort((a, b) => Number(a.prix) - Number(b.prix));
    } else if (sortBy === 'price-desc') {
      sorted.sort((a, b) => Number(b.prix) - Number(a.prix));
    } else if (sortBy === 'name-asc') {
      sorted.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    } else if (sortBy === 'new') {
      sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }
    return sorted;
  };

  const title = KNOWN_SLUGS.includes(categorie)
    ? t(`category.names.${categorie}`)
    : (categorie.charAt(0).toUpperCase() + categorie.slice(1));

  const sortedProducts = getSortedProducts();

  return (
    <main className="category-page">

      {/* ⭐ SEO Helper — placé dans le JSX */}
      <SEOHelper
        title={category?.meta_title || title}
        description={category?.meta_description || `Découvrez notre collection ${title} chez MADAD`}
      />

      <nav className="category-page__breadcrumb">
        <div className="category-page__breadcrumb-inner">
          <Link to="/">{t('category.home')}</Link>
          <span>›</span>
          <Link to="/boutique">{t('category.shop')}</Link>
          <span>›</span>
          <span className="category-page__breadcrumb-current">{title}</span>
        </div>
      </nav>

      <header className="category-page__head">
        <div className="category-page__head-inner">
          <span className="category-page__label">{t('category.collection')}</span>
          <h1 className="category-page__title">{title}</h1>
          {!loading && !error && (
            <p className="category-page__count">
              {sortedProducts.length} {sortedProducts.length > 1 ? t('category.pieces') : t('category.piece')}
            </p>
          )}
        </div>
      </header>

      {!loading && !error && sortedProducts.length > 0 && (
        <div className="category-page__toolbar">
          <div className="category-page__toolbar-inner">
            <span className="category-page__results">
              {t('category.showingResults', {
                n: sortedProducts.length,
                plural: sortedProducts.length > 1 ? 's' : '',
              })}
            </span>

            <div className="category-page__sort">
              <label htmlFor="sort">{t('category.sortBy')}</label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="default">{t('category.sortRelevance')}</option>
                <option value="new">{t('category.sortNew')}</option>
                <option value="price-asc">{t('category.sortPriceAsc')}</option>
                <option value="price-desc">{t('category.sortPriceDesc')}</option>
                <option value="name-asc">{t('category.sortNameAsc')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <section className="category-page__content">
        {loading && (
          <div className="category-page__loading"><div className="spinner" /></div>
        )}

        {!loading && error && (
          <div className="category-page__empty">
            <div className="category-page__empty-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3>{t('category.errorTitle')}</h3>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              {t('category.retry')}
            </button>
          </div>
        )}

        {!loading && !error && sortedProducts.length === 0 && (
          <div className="category-page__empty">
            <div className="category-page__empty-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <path d="M16 10a4 4 0 0 1-8 0" />
              </svg>
            </div>
            <h3>{t('category.emptyTitle')}</h3>
            <p>{t('category.emptyText')}</p>
            <Link to="/boutique" className="btn btn-primary">
              {t('category.discoverShop')}
            </Link>
          </div>
        )}

        {!loading && !error && sortedProducts.length > 0 && (
          <div className="category-page__grid">
            {sortedProducts.map(p => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

export default CategoryPage;